import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Search, Phone, MessageCircle, ChevronRight, Scale, Anchor, Car, Shield, Heart, BookOpen, Calculator, MapPin, HelpCircle } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const SitemapPage = () => {
  const sections = [
    {
      title: 'Main Pages',
      icon: Home,
      links: [
        { name: 'Home', path: '/' },
        { name: 'About Amy', path: '/about' },
        { name: 'Attorney Credentials', path: '/about/credentials' },
        { name: 'Working With a Woman Attorney', path: '/woman-personal-injury-lawyer' },
        { name: 'Working With a Solo Attorney', path: '/solo-personal-injury-lawyer' },
        { name: 'Contact', path: '/contact' }
      ]
    },
    {
      title: 'Practice Areas',
      icon: Scale,
      links: [
        { name: 'Maritime Injuries & Jones Act', path: '/maritime-injuries' },
        { name: 'Car Crashes', path: '/car-crashes' },
        { name: 'Motorcycle Crashes', path: '/motorcycle-crashes' },
        { name: 'First Responder Crashes', path: '/first-responder-crashes' },
        { name: 'Slip and Falls', path: '/slip-and-falls' },
        { name: 'Wrongful Death', path: '/wrongful-death' }
      ]
    },
    {
      title: 'Resources',
      icon: BookOpen,
      links: [
        { name: 'Learning Center (Blog)', path: '/blog' },
        { name: 'FAQs', path: '/faqs' },
        { name: 'Legal Glossary', path: '/legal-glossary' },
        { name: 'Pricing & Costs', path: '/pricing' },
        { name: 'Compensation Estimator', path: '/compensation-estimator' },
        { name: 'Is Amy Right For You?', path: '/is-amy-right-for-you' },
        { name: 'How Insurance Companies Handle Claims', path: '/how-insurance-companies-handle-claims' }
      ]
    },
    {
      title: 'Service Areas',
      icon: MapPin,
      links: [
        { name: 'Areas We Serve', path: '/areas-we-serve' },
        { name: 'Seattle', path: '/service-area/seattle-personal-injury-lawyer' },
        { name: 'Everett', path: '/service-area/everett-personal-injury-lawyer' },
        { name: 'Bellevue', path: '/service-area/bellevue-personal-injury-lawyer' },
        { name: 'Spokane', path: '/service-area/spokane-personal-injury-lawyer' },
        { name: 'Olympia', path: '/service-area/olympia-personal-injury-lawyer' },
        { name: 'Vancouver', path: '/service-area/vancouver-wa-personal-injury-lawyer' },
        { name: 'Yakima', path: '/service-area/yakima-personal-injury-lawyer' },
        { name: 'Kent', path: '/service-area/kent-personal-injury-lawyer' },
        { name: 'Renton', path: '/service-area/renton-personal-injury-lawyer' },
        { name: 'Kirkland', path: '/service-area/kirkland-personal-injury-lawyer' },
        { name: 'Redmond', path: '/service-area/redmond-personal-injury-lawyer' },
        { name: 'Auburn', path: '/service-area/auburn-personal-injury-lawyer' },
        { name: 'Federal Way', path: '/service-area/federal-way-personal-injury-lawyer' },
        { name: 'Marysville', path: '/service-area/marysville-personal-injury-lawyer' },
        { name: 'Lakewood', path: '/service-area/lakewood-personal-injury-lawyer' },
        { name: 'Edmonds', path: '/service-area/edmonds-personal-injury-lawyer' }
      ]
    },
    {
      title: 'Specialized Pages',
      icon: Shield,
      links: [
        { name: 'Everett Personal Injury Lawyer', path: '/everett-personal-injury-lawyer' },
        { name: 'Everett Car Accident Lawyer', path: '/everett-car-accident-lawyer' },
        { name: 'Snohomish County Car Accident Lawyer', path: '/snohomish-county-car-accident-lawyer' },
        { name: 'Rear-End Accident Lawyer', path: '/rear-end-accident-lawyer' },
        { name: 'Drunk Driver Accident Lawyer', path: '/drunk-driver-accident-lawyer' },
        { name: 'What To Do After a Car Accident', path: '/what-to-do-after-car-accident' },
        { name: 'Should I Talk to Insurance?', path: '/should-i-talk-to-insurance' },
        { name: 'Should I Accept the Settlement?', path: '/should-i-accept-settlement' },
        { name: 'How Much Is My Case Worth?', path: '/how-much-is-my-case-worth' },
        { name: 'How Long Does a Settlement Take?', path: '/how-long-does-settlement-take' },
        { name: 'When to Hire a Car Accident Lawyer', path: '/when-to-hire-car-accident-lawyer' },
        { name: 'Everett Slip and Fall Lawyer', path: '/everett-slip-and-fall-lawyer' },
        { name: 'Tacoma Slip and Fall Lawyer', path: '/tacoma-slip-and-fall-lawyer' },
        { name: 'Bellingham Slip and Fall Lawyer', path: '/bellingham-slip-and-fall-lawyer' }
      ]
    },
    {
      title: 'Legal',
      icon: HelpCircle,
      links: [
        { name: 'Privacy Policy', path: '/privacy-policy' },
        { name: 'Terms of Service', path: '/terms-of-service' }
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="sitemap-page">
      <SEOHead 
        title="Sitemap | Amy C. Brown Law"
        description="Complete sitemap of Amy C. Brown Law website. Find all pages including practice areas, service locations, resources, and legal information."
      />

      {/* Hero */}
      <section className="py-12 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
            Sitemap
          </h1>
          <p className="text-lg text-slate-300">
            Find all pages on the Amy C. Brown Law website
          </p>
        </div>
      </section>

      {/* Sitemap Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sections.map((section, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <section.icon className="w-5 h-5 text-teal-600" />
                  <h2 className="font-heading text-xl font-semibold text-secondary">
                    {section.title}
                  </h2>
                </div>
                <ul className="space-y-2">
                  {section.links.map((link, i) => (
                    <li key={i}>
                      <Link 
                        to={link.path}
                        className="flex items-center gap-2 text-slate-600 hover:text-teal-600 transition-colors py-1"
                      >
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-2xl font-bold text-secondary mb-4">
            Can't Find What You're Looking For?
          </h2>
          <p className="text-slate-600 mb-6">
            Contact me directly and I'll be happy to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-teal-700 transition-colors"
            >
              <Phone className="w-5 h-5" />
              (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-teal-600 text-teal-600 px-6 py-3 rounded-full font-semibold hover:bg-teal-50 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SitemapPage;
